import angular from 'angular';

/*******************************************************************************
* @ngdoc    object
* @module   csm.books.InitialSetup.types
* @name     csm.books.InitialSetup.types.type:ArrayCollection
* @constructor
* @param {array}     source          Source array.
* @param {function=} filterFunction  `source.filter()` predicate function.
*
* @description
* Array of objects maintained as a subset of a `source` array. `filterFunction`
* determines the objects in `source` to include. This is an Array with all 
* destructive methods removed and methods added for modifying the collection and
* the `source` array. This was built specifically to be used as a data provider
* for lists of objects added, changed, or removed in a grid.
*
* TODO: Using brackets ({}) or the `delete` operator to modify this array will
* TODO: not translate to the `_source` array because these features cannot be
* TODO: overridden in IE, which does not support the "iterable protocol". Once 
* TODO: we standardize on Edge 12 or later, this type can be reimplemented to 
* TODO: remove this vulnerability.
* TODO: see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Iteration_protocols#The_iterable_protocol
*******************************************************************************/
/* @ngInject */ export function arrayCollectionFactory(AutoIDList) {
  function ArrayCollection(source, filterFunction) {
    if (
      !(source instanceof Array) ||
      (!!filterFunction && !angular.isFunction(filterFunction))
    )
      throw 'usage: new ArrayCollection(Array [, FilterFunction])';

    this._source = source;
    this._filterFunction = filterFunction;
    this.refresh();
  }

  ArrayCollection.prototype = Object.defineProperties(
    Object.create(Array.prototype),
    {
      /*---------*/
      /* Private */
      /*---------*/
      _filterFunction: { value: null, writable: true },
      _source: { value: null, writable: true },

      /**
       * @ngdoc       property
       * @name        filterFunction
       * @propertyOf  csm.books.InitialSetup.types.type:ArrayCollection
       * @description
       * Predicate function to apply with `source.filter()`. `refresh()` must be
       * called to reload the collection.
       */
      filterFunction: {
        set: function (f) {
          if (!angular.isFunction(f))
            throw 'ArrayCollection.filterFunction: value must be a function';
          this._filterFunction = f;
        }
      },

      /**
       * @ngdoc       property
       * @name        source
       * @propertyOf  csm.books.InitialSetup.types.type:ArrayCollection
       * @description
       * Source array of collection. `refresh()` must be called to reload the
       * collection.
       */
      source: {
        set: function (s) {
          if (!Array.isArray(s))
            throw 'ArrayCollection.source: value must be an array';
          this._source = s;
        }
      },

      /***************************************************************************
       * addItem */
      /**
       * @ngdoc       function
       * @name        addItem
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @param       {object} item Object to add.
       * @return      Item added.
       * @description Adds `item` to the end of the collection and `_source` array.
       ***************************************************************************/
      addItem: {
        value: function (item) {
          if (!angular.isObject(item))
            throw 'usage: ArrayCollection.addItem(Object)';

          this._source.push(item);
          Array.prototype.push.call(this, item);

          return item;
        }
      },

      /***************************************************************************
       * refresh */
      /**
       * @ngdoc       function
       * @name        refresh
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @returns     {ArrayCollection}  This collection object.
       * @description Recreates collection from `_source` using `_filterFunction`.
       ***************************************************************************/
      refresh: {
        value: function () {
          var list = this._source.filter(
            this._filterFunction ||
              function () {
                return true;
              }
          );
          Array.prototype.splice.apply(this, [0, this.length].concat(list));
          return this;
        }
      },

      /***************************************************************************
       * removeItem */
      /**
       * @ngdoc       function
       * @name        removeItem
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @param       {object} item  Item to remove.
       * @return      {object}       Removed item.
       * @description
       * Removes `item` from the collection and from the `_source` array.
       ***************************************************************************/
      removeItem: {
        value: function (item) {
          return replaceItem.call(this, 'removeItem(item)', item);
        }
      },

      /***************************************************************************
       * removeItemAt */
      /**
       * @ngdoc       function
       * @name        removeItemAt
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @param       {integer} index Index of item to remove.
       * @return      {object}        Removed item.
       * @description
       * Removes the item at `index` from the collection and from wherever the
       * object is found in `_source`.
       *
       * Note: **Use this instead of the `delete` operator.**
       ***************************************************************************/
      removeItemAt: {
        value: function (index) {
          return replaceItemAt.call(this, 'removeItemAt(index)', index);
        }
      },

      /***************************************************************************
       * replaceItem */
      /**
       * @ngdoc       function
       * @name        replaceItem
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @param       {object} item    Item to remove.
       * @param       {object} newItem Replacement item.
       * @return      {object}         Removed item.
       * @description
       * Replaces `item` with `newItem` in the collection and `_source` array.
       ***************************************************************************/
      replaceItem: {
        value: function (item, newItem) {
          return replaceItem.call(
            this,
            'replaceItem(item, newItem)',
            item,
            newItem
          );
        }
      },

      /***************************************************************************
       * replaceItemAt */
      /**
       * @ngdoc       function
       * @name        replaceItemAt
       * @methodOf    csm.books.InitialSetup.types.type:ArrayCollection
       * @param       {integer} index   Index of item to remove.
       * @param       {object}  newItem Replacement item.
       * @return      {object}          Removed item.
       * @description
       * Replaces the item at `index` with `newItem` in the collection and wherever
       * the replaced object is found in `_source`.
       *
       * Note: **Use this instead of bracket ([]) assignment.**
       ***************************************************************************/
      replaceItemAt: {
        value: function (index, newItem) {
          return replaceItemAt.call(
            this,
            'replaceItemAt(index, newItem)',
            index,
            newItem
          );
        }
      },

      /***************************************************************************
       * toJSON */
      /**
       * This is an object so this method is necessary to serialize properly.
       ***************************************************************************/
      toJSON: {
        value: function () {
          return this.map(function (e) {
            return e;
          });
        }
      }
    }
  );

  /*---------------------------------------------*/
  /* Make destructive Array methods unavailable. */
  /*---------------------------------------------*/
  [
    'fill',
    'pop',
    'push',
    'reverse',
    'shift',
    'sort',
    'splice',
    'unshift'
  ].forEach(function (method) {
    Object.defineProperty(ArrayCollection.prototype, method, {
      value: new Function(
        'throw "ArrayCollection.' + method + ' not implemented";'
      )
    });
  });

  return ArrayCollection;

  /***************************************************************************
   * replaceItem */
  /**
   * Removes or replaces `item` with `newItem` in collection and source array.
   * This is done like this because there is no way to overload the iterator in
   * an array in IE. Any direct assignment using brackets ([]) or use of the
   * `delete` operator will not translate to the `_source` array.
   *
   * @param  {fname}  fname    Name of calling function, for exception msgs.
   * @param  {object} item     Item to remove/replace
   * @param  {object} newItem  New item, replace only.
   * @return {object}          Removed item.
   ***************************************************************************/
  function replaceItem(fname, item, newItem) {
    if (angular.isUndefined(item))
      throw 'ArrayCollection.' + fname + ': item is not defined.';

    var index = this.indexOf(item);

    if (index < 0)
      throw 'ArrayCollection.' + fname + ': item not found in collection.';

    return replaceItemAt.call(this, fname, index, newItem);
  }

  /***************************************************************************
   * replaceItemAt */
  /**
   * Removes or replaces item at `index` with `newItem` in collection and
   * source array. This is done like this because there is no way to overload
   * the iterator in array in IE. Any direct assignment using brackets ([]) or
   * use of the `delete` operator will not translate to the `_source` array.
   *
   * @param  {fname}   fname    Name of calling function, for exception msgs.
   * @param  {integer} index    Index of item to remove/replace
   * @param  {object}  newItem  New item, replace only.
   * @return {object}           Removed item.
   ***************************************************************************/
  function replaceItemAt(fname, index, newItem) {
    if (angular.isUndefined(index))
      throw 'ArrayCollection.' + fname + ': index is undefined.';

    if (index < 0 || index >= this.length)
      throw 'ArrayCollection.' + fname + ': index out of range.';

    var item = this[index];
    var sindex = this._source.indexOf(item);
    var newitems = newItem ? [newItem] : [];

    if (index < 0)
      throw 'ArrayCollection.' + fname + ': item not found in source array.';

    Array.prototype.splice.apply(this, [index, 1].concat(newitems));
    Array.prototype.splice.apply(this._source, [sindex, 1].concat(newitems));

    return item;
  }
};
